import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useNavigationType, useParams } from "react-router-dom";
import { memoriserPosition, positionMemorisee, restaurerPosition } from "./utils/defilement";
import { Toaster, toast } from "sonner";
// Sentry n'est plus importé statiquement (27/10/2026) : chargé après l'affichage
// par utils/sentryDiffere, hors du fichier de démarrage.
import { avecSentry } from "./utils/sentryDiffere";
import DebugConsole from "./components/DebugConsole";
import { AuthProvider, useAuth, api, onLegalGate } from "./context/AuthContext";
import { AssociationAuthProvider, useAssociationAuth } from "./context/AssociationAuthContext";
import VersementsAiguillage from "./pages/versements/VersementsAiguillage";
import { DesktopHeader, BottomNavigation, LegalFooter } from "./components/Layout";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import CookieConsent from "./components/CookieConsent";
import SplashScreen from "./components/SplashScreen";
import OfflineBanner from "./components/OfflineBanner";
import PushPermissionPrimer from "./components/PushPermissionPrimer";
import AssoFeedbackWidget from "./components/AssoFeedbackWidget";
import NativeBootstrap from "./native/NativeBootstrap";
// Phase 5+ — Container de notifications gamification (toasts badges
// débloqués, level-up, etc.) — composant existant mais orphelin avant.
import { GamificationNotificationContainer } from "./components/GamificationComponents";
import LegalUpdateModal from "./components/LegalUpdateModal";
import SynchroAffichage from "./components/SynchroAffichage";
import LegalGraceBanner from "./components/LegalGraceBanner";
import { etatMurJuridique, estPageDocumentJuridique, murAAfficher } from "./utils/legalGate";
import { lireConsentementOauth } from "./utils/oauthConsent";
import { safeSessionStorage } from "./utils/safeStorage";
import { captureAssoReferralFromUrl } from "./utils/assoReferral";
import { signalerMurConnexion } from "./utils/murConnexion";
// Page d'accueil importée en STATIQUE (dans le bundle principal) : c'est la
// page la plus visitée/crawlée. En lazy-chunk, elle ajoutait un aller-retour
// réseau (chunk séparé) avant la 1ʳᵉ peinture → LCP plus lent + surface à
// ChunkLoadError sur la page d'entrée. En statique : peinture plus rapide.
import HomePage from "./pages/home/HomePage";
import { isPhoenixPublishEnabled } from "./pages/publish/state/featureFlags";

// ─── Lazy loader with retry (handles stale chunks after deploy) ──────────────
// Phase 27F8.6 — logs CHUNK_LOAD_START / SUCCESS / ERROR avec nom strict
// pour grep iPhone debugging.
let _chunkSeq = 0;
const lazyRetry = (importFn) => lazy(() => {
  const id = ++_chunkSeq;
  // eslint-disable-next-line no-console
  console.log("[CHUNK_LOAD_START]", { id });
  return new Promise((resolve, reject) => {
    importFn()
      .then((mod) => {
        // eslint-disable-next-line no-console
        console.log("[CHUNK_LOAD_SUCCESS]", { id });
        resolve(mod);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[CHUNK_LOAD_ERROR]", {
          id,
          error: String(err?.message || err).slice(0, 200),
        });
        // Stale chunk : force reload once.
        if (!safeSessionStorage.getItem("gively_chunk_reload")) {
          safeSessionStorage.setItem("gively_chunk_reload", "1");
          window.location.reload();
          return;
        }
        // Already retried — final retry then reject.
        importFn().then(resolve).catch(reject);
      });
  });
});

// ─── Pages lazy-loaded ────────────────────────────────────────────────────────
// Home
// HomePage : import statique (voir en-tête) — plus de lazyRetry ici.
// PWA Web Share Target (point d'entrée des partages entrants → /publish)
const ShareTargetPage   = lazyRetry(() => import("./pages/share/ShareTargetPage"));
// PWA File Handlers (ouvrir une image AVEC Gively → /publish)
const OpenFilePage      = lazyRetry(() => import("./pages/share/OpenFilePage"));
// Search
const SearchPage        = lazyRetry(() => import("./pages/search/SearchPage"));
const SavedSearchesPage = lazyRetry(() => import("./pages/search/SavedSearchesPage"));
// Listing
const ListingDetailPage = lazyRetry(() => import("./pages/listing/ListingDetailPage"));
// Checkout (tunnel acheteur dédié — sortie de la fiche annonce)
const CheckoutPage      = lazyRetry(() => import("./pages/checkout/CheckoutPage"));
// Boost (tunnel vendeur dédié pleine page — remplace l'ancien BoostModal widget)
const BoostPage         = lazyRetry(() => import("./pages/listing/BoostPage"));
const BoostStripePage   = lazyRetry(() => import("./pages/listing/BoostStripePage"));
const StripePurchasePage = lazyRetry(() => import("./pages/payment/StripePurchasePage"));
const LegacyPaypalPaymentRedirect = lazyRetry(() => import("./pages/payment/LegacyPaypalPaymentRedirect"));
// Phase 8.13 — Page Paiement Ultime (UNIQUE pour TOUS les paiements
// Gively : boost actuellement, achat en Phase 2). Demande user
// 03/05/2026 : "qu'une seule page avec qu'un seul code".
// Publish
const NewPublishPage    = lazyRetry(() => import("./pages/publish/NewPublishPage"));
const PublishWizard     = lazyRetry(() => import("./pages/publish/PublishWizard"));
const PublishEntry      = lazyRetry(() => import("./pages/publish/PublishEntry"));
// Associations
const AssociationsPage     = lazyRetry(() => import("./pages/associations/AssociationsPage"));
const AssociationDetailPage = lazyRetry(() => import("./pages/associations/AssociationDetailPage").then(m => ({ default: m.AssociationDetailPage })));
// Auth
const LoginPage         = lazyRetry(() => import("./pages/auth/LoginPage").then(m => ({ default: m.LoginPage })));
const RegisterPage      = lazyRetry(() => import("./pages/auth/LoginPage").then(m => ({ default: m.RegisterPage })));
const VerifyEmailPage   = lazyRetry(() => import("./pages/auth/VerifyEmailPage"));
const ForgotPasswordPage = lazyRetry(() => import("./pages/auth/AuthPages").then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage  = lazyRetry(() => import("./pages/auth/AuthPages").then(m => ({ default: m.ResetPasswordPage })));
// Dashboard
// Dashboard — DashboardPage removed (replaced by /profile hub + /mes-annonces)
const FavoritesPage     = lazyRetry(() => import("./pages/dashboard/FavoritesPage"));
const NotificationsPage = lazyRetry(() => import("./pages/dashboard/NotificationsPage"));
// Profile
const ProfilePage       = lazyRetry(() => import("./pages/profile/ProfilePage"));
const EditProfilePage   = lazyRetry(() => import("./pages/profile/EditProfilePage"));
const PublicProfilePage = lazyRetry(() => import("./pages/profile/PublicProfilePage"));
const SettingsPage      = lazyRetry(() => import("./pages/profile/SettingsPage"));
// Centre de préférences email — page PUBLIQUE (lien « Se désabonner » tokenisé
// des emails, audit RGPD 2026-07) ; fonctionne aussi connecté via /settings.
const PreferencesEmailPage = lazyRetry(() => import("./pages/settings/PreferencesEmailPage"));
const WalletPage        = lazyRetry(() => import("./pages/profile/WalletPage"));
// Phase 5+ — Page gamification dédiée (Mon impact solidaire).
const MyImpactPage      = lazyRetry(() => import("./pages/profile/MyImpactPage"));
// VerificationPage removed — redirects to /wallet
const SimulationPage    = lazyRetry(() => import("./pages/profile/SimulationPage"));
// Messages
const MessagesPage      = lazyRetry(() => import("./pages/messages/MessagesPage").then(m => ({ default: m.MessagesPage })));
const ConversationPage  = lazyRetry(() => import("./pages/messages/MessagesPage").then(m => ({ default: m.ConversationPage })));
// Admin V2 — RNA / Directory / Imports / Enrichment / Prospection pages removed (sunset 2026-04)
const AdminLayout          = lazyRetry(() => import("./pages/admin-v2/AdminLayout"));
const AdminDashboardV2     = lazyRetry(() => import("./pages/admin-v2/DashboardPage"));
const AdminAssociationsV2  = lazyRetry(() => import("./pages/admin-v2/AssociationsPage"));
const AdminCampaignV2      = lazyRetry(() => import("./pages/admin-v2/CampaignPage"));
const AdminFollowUpV2      = lazyRetry(() => import("./pages/admin-v2/FollowUpPage"));
const AdminBlogV2          = lazyRetry(() => import("./pages/admin-v2/BlogPage"));
const AdminUsersV2         = lazyRetry(() => import("./pages/admin-v2/UsersPage"));
const AdminListingsV2      = lazyRetry(() => import("./pages/admin-v2/ListingsPage"));
const AdminReportsV2       = lazyRetry(() => import("./pages/admin-v2/ReportsPage"));
const AdminDisputesV2      = lazyRetry(() => import("./pages/admin-v2/DisputesPage"));
const AdminDepositsV2      = lazyRetry(() => import("./pages/admin-v2/DepositsPage"));
const AdminMessagingV2     = lazyRetry(() => import("./pages/admin-v2/MessagingPage"));
const AdminKpiV2           = lazyRetry(() => import("./pages/admin-v2/KpiPage"));
const AdminCoherenceV2     = lazyRetry(() => import("./pages/admin-v2/CoherencePage"));
const AdminPlatformHealth  = lazyRetry(() => import("./pages/admin-v2/PlatformHealthPage"));
const AdminLaunchMetrics   = lazyRetry(() => import("./pages/admin-v2/LaunchMetricsPage"));
const AdminDocumentsV2     = lazyRetry(() => import("./pages/admin-v2/DocumentsPage"));
const AdminEmailsV2        = lazyRetry(() => import("./pages/admin-v2/EmailsPage"));
const AdminAppStoreAssets  = lazyRetry(() => import("./pages/admin-v2/AppStoreAssetsPage"));
// Phase 28 — page « Virements manuels » (PayoutsPageSimple) retirée du routing
// (redondante + cassée : mark-paid sans champs SEPA → 422 → crash). Tout le
// reversement passe par « Transactions » (/admin/transactions).
// Archivage 24/08/2026 (audit admin #2197) : les filets hors menu
// /admin/payouts-legacy et /admin/sales (liste) sont SUPPRIMÉS — zéro lien
// entrant, redondants avec Transactions ; l'historique Git les conserve.
const AdminAssoProspect    = lazyRetry(() => import("./pages/admin-v2/AssoProspectPage"));
// Phase 13.39 — Tableau de bord prospection "B -> A" (federations,
// coordinations, mairies, MDA, DLA, CCAS, fondations).
const AdminFederations     = lazyRetry(() => import("./pages/admin-v2/FederationsPage"));
// 07/10/2026 — Commerçants toulousains : liste officielle + tournée.
const AdminCommercants     = lazyRetry(() => import("./pages/admin-v2/CommercantsPage"));
const AdminAcquisition     = lazyRetry(() => import("./pages/admin-v2/AcquisitionPage"));
const AdminReferencement   = lazyRetry(() => import("./pages/admin-v2/ReferencementPage"));
// Phase 13.62 — Presse separee de Federations (Lucas : 'rubrique dediee')
const AdminPress           = lazyRetry(() => import("./pages/admin-v2/PressPage"));
const AdminSaleDetailV2    = lazyRetry(() => import("./pages/admin-v2/SaleDetailPage"));
const AdminAlertsV2        = lazyRetry(() => import("./pages/admin-v2/AlertsPage"));
const AdminBlockedCasesV2  = lazyRetry(() => import("./pages/admin-v2/BlockedCasesPage"));
// Production Watch (D-2026-07-03-9.5) — verdict production en 30 s.
const AdminProductionWatch = lazyRetry(() => import("./pages/admin-v2/ProductionWatchPage"));
const AdminBoxtalV2        = lazyRetry(() => import("./pages/admin-v2/BoxtalDiagnosticPage"));
const AdminToolsV2         = lazyRetry(() => import("./pages/admin-v2/ToolsPage"));
// Phase 6+ — Tableau unifié transactions (ventes + boosts).
const AdminTransactionsV2  = lazyRetry(() => import("./pages/admin-v2/TransactionsPage"));
const AdminAccountingV2     = lazyRetry(() => import("./pages/admin-v2/AccountingPage"));
const AdminFinanceHub       = lazyRetry(() => import("./pages/admin-v2/finance/FinanceHub"));
const AdminStripeSimulation = lazyRetry(() => import("./pages/admin-v2/finance/StripeSimulationPage"));
// DAC7 — Assistant déclaration annuelle (Finance → DAC7, spec 2026-07-14).
const AdminDac7V2           = lazyRetry(() => import("./pages/admin-v2/Dac7Page"));
// PayPal MVP — Stripe Connect accounts console & manual payouts page retired.
// Blog (public)
const BlogListPage         = lazyRetry(() => import("./pages/blog/BlogListPage"));
const BlogDetailPage       = lazyRetry(() => import("./pages/blog/BlogDetailPage"));
// Association space
const AssociationLandingPage  = lazyRetry(() => import("./pages/association-space/LandingPage"));
const AssociationLoginPage    = lazyRetry(() => import("./pages/association-space/LoginPage"));
const AssociationRegisterPage = lazyRetry(() => import("./pages/association-space/RegisterPage"));
const AssociationSetupPasswordPage = lazyRetry(() => import("./pages/association-space/SetupPasswordPage"));
const AssociationResetPasswordPage = lazyRetry(() => import("./pages/association-space/ResetPasswordPage"));
const AssociationDashboardPage = lazyRetry(() => import("./pages/association-space/DashboardPage"));
// Réclamations — rubrique commune (particulier / pro / association).
const ReclamationsPage = lazyRetry(() => import("./pages/claims/ReclamationsPage"));
// Phase 11 — page dediee Google Ad Grants pour les assos partenaires :
// programme, etapes, badge "Partenaire Gively" telechargeable.
const AssociationAdGrantsPage = lazyRetry(() => import("./pages/association-space/AdGrantsPage"));
const AssociationAffichageLibrePage = lazyRetry(() => import("./pages/association-space/AffichageLibrePage"));
const AssociationCGUPage      = lazyRetry(() => import("./pages/association-space/CGUPage"));
const AssociationChartePage   = lazyRetry(() => import("./pages/association-space/ChartePage"));
const RevendreCadeauxNoelPage = lazyRetry(() => import("./pages/static/RevendreCadeauxNoelPage"));  // plan Noël 05/11/2026
// VerificationAssoPage removed — redirects to /association/dashboard
// Legal
const CGUPage           = lazyRetry(() => import("./pages/legal/LegalPages").then(m => ({ default: m.CGUPage })));
// SEO Landing Pages
const AssociationJoinPage = lazyRetry(() => import("./pages/seo/SEOLandingPages").then(m => ({ default: m.AssociationJoinPage })));
const MarketplaceSolidairePage = lazyRetry(() => import("./pages/seo/SEOLandingPages").then(m => ({ default: m.MarketplaceSolidairePage })));
// SEO Growth Pages
const VendreAvecDonPage = lazyRetry(() => import("./pages/seo/SEOGrowthPages").then(m => ({ default: m.VendreAvecDonPage })));
const ReventeSolidairePage = lazyRetry(() => import("./pages/seo/SEOGrowthPages").then(m => ({ default: m.ReventeSolidairePage })));
const FinancerAssociationPage = lazyRetry(() => import("./pages/seo/SEOGrowthPages").then(m => ({ default: m.FinancerAssociationPage })));
const ConsommerResponsablePage = lazyRetry(() => import("./pages/seo/SEOGrowthPages").then(m => ({ default: m.ConsommerResponsablePage })));
const LouerEntreParticuliersPage = lazyRetry(() => import("./pages/seo/SEOGrowthPages").then(m => ({ default: m.LouerEntreParticuliersPage })));
const ServicesSolidairesPage = lazyRetry(() => import("./pages/seo/SEOGrowthPages").then(m => ({ default: m.ServicesSolidairesPage })));
const MarketplaceSolidaireFAQPage = lazyRetry(() => import("./pages/seo/SEOGrowthPages").then(m => ({ default: m.MarketplaceSolidaireFAQPage })));
const SearchLandingPage = lazyRetry(() => import("./pages/seo/SearchLandingPage"));
// Phase 12.3 — Programmatic SEO : 1 page département + 4 landings longue traîne.
const DepartmentAssociationsPage = lazyRetry(() => import("./pages/seo/DepartmentAssociationsPage"));
const CollecterFondsAssociationPage = lazyRetry(() => import("./pages/seo/LongTailLandings").then(m => ({ default: m.CollecterFondsAssociationPage })));
const VenteObjetDonPage = lazyRetry(() => import("./pages/seo/LongTailLandings").then(m => ({ default: m.VenteObjetDonPage })));
const VideGrenierSolidairePage = lazyRetry(() => import("./pages/seo/LongTailLandings").then(m => ({ default: m.VideGrenierSolidairePage })));
const CGVPage           = lazyRetry(() => import("./pages/legal/LegalPages").then(m => ({ default: m.CGVPage })));
const PrivacyPage       = lazyRetry(() => import("./pages/legal/LegalPages").then(m => ({ default: m.PrivacyPage })));
const InformationsLegalesPage = lazyRetry(() => import("./pages/legal/InformationsLegalesPage"));
const MentionsLegalesPage = lazyRetry(() => import("./pages/legal/LegalPages").then(m => ({ default: m.MentionsLegalesPage })));
const ReferencementClassementPage = lazyRetry(() => import("./pages/legal/ReferencementClassementPage"));
const ContratPartenariatPage = lazyRetry(() => import("./pages/legal/LegalPages").then(m => ({ default: m.ContratPartenariatPage })));
const MandatCollectePage = lazyRetry(() => import("./pages/legal/LegalPages").then(m => ({ default: m.MandatCollectePage })));
const FiscaliteDonsPage = lazyRetry(() => import("./pages/legal/LegalPages").then(m => ({ default: m.FiscaliteDonsPage })));
const AccountDeletionPage = lazyRetry(() => import("./pages/legal/LegalPages").then(m => ({ default: m.AccountDeletionPage })));
// Static
const HowItWorksPage    = lazyRetry(() => import("./pages/static/StaticPages").then(m => ({ default: m.HowItWorksPage })));
const NotFoundPage      = lazyRetry(() => import("./pages/static/StaticPages").then(m => ({ default: m.NotFoundPage })));
const ContactPage       = lazyRetry(() => import("./pages/static/StaticPages").then(m => ({ default: m.ContactPage })));
const FAQPage           = lazyRetry(() => import("./pages/static/StaticPages").then(m => ({ default: m.FAQPage })));
const HelpPage          = lazyRetry(() => import("./pages/static/StaticPages").then(m => ({ default: m.HelpPage })));
const PressPage         = lazyRetry(() => import("./pages/static/PressPage"));
// Phase 13.62 — Communique de presse imprimable A4 (Imprimer -> PDF depuis le navigateur)
const PressReleasePage  = lazyRetry(() => import("./pages/static/PressReleasePage"));
const CategoryPage      = lazyRetry(() => import("./pages/seo/CategoryPage"));
const CityCategoryPage  = lazyRetry(() => import("./pages/seo/CityCategoryPage"));
const CityRentalPage    = lazyRetry(() => import("./pages/seo/CityRentalPage"));
const CityServicePage   = lazyRetry(() => import("./pages/seo/CityServicePage"));
const CauseAssociationsPage = lazyRetry(() => import("./pages/seo/CauseAssociationsPage"));
const NewsletterPage    = lazyRetry(() => import("./pages/blog/NewsletterPage"));
const QuiSommesNousPage = lazyRetry(() => import("./pages/static/QuiSommesNousPage"));
const PartenairesPage   = lazyRetry(() => import("./pages/static/PartenairesPage"));
const CharteEthiquePage = lazyRetry(() => import("./pages/static/CharteEthiquePage"));
const PourLesAssociationsPage = lazyRetry(() => import("./pages/static/PourLesAssociationsPage"));
// 07/10/2026 — page des commerçants (QR code de la tournée, lien des e-mails).
const CommercantsLandingPage = lazyRetry(() => import("./pages/static/CommercantsLandingPage"));
const CommentFinancerAssociationPage = lazyRetry(() => import("./pages/static/CommentFinancerAssociationPage"));
const CommentFinancerClubSportPage = lazyRetry(() => import("./pages/static/CommentFinancerClubSportPage"));
const CommentFinancerProjetScolairePage = lazyRetry(() => import("./pages/static/CommentFinancerProjetScolairePage"));
const CommentFinancerAssociationEtudiantePage = lazyRetry(() => import("./pages/static/CommentFinancerAssociationEtudiantePage"));
const OrganiserCollecteFondsPage = lazyRetry(() => import("./pages/static/OrganiserCollecteFondsPage"));
const CommentVendreSecuritePage = lazyRetry(() => import("./pages/static/CommentVendreSecuritePage"));
// 01/11/2026 — audit comparé à Leboncoin : « où est mon argent ? » expliqué au vendeur.
const ArgentVentesPage = lazyRetry(() => import("./pages/static/ArgentVentesPage"));
const RecuFiscalPage = lazyRetry(() => import("./pages/static/RecuFiscalPage"));
const AssosFondatricesPage = lazyRetry(() => import("./pages/static/AssosFondatricesPage"));
const WelcomePage       = lazyRetry(() => import("./pages/static/WelcomePage"));
const ChooseAssoPage    = lazyRetry(() => import("./pages/listing/ChooseAssoPage"));
const ReviewPage        = lazyRetry(() => import("./pages/static/ReviewPage"));
// Purchases & Sales
const PurchaseHistoryPage = lazyRetry(() => import("./pages/purchases/PurchaseHistoryPage"));
const ConfirmReceiptPage  = lazyRetry(() => import("./pages/ConfirmReceiptPage"));
const MyListingsPage      = lazyRetry(() => import("./pages/listings/MyListingsPage"));
const MyDonationsPage     = lazyRetry(() => import("./pages/profile/MyDonationsPage"));
// Architecture « Mes transactions » (29/09/2026) : un seul endroit pour ce que
// l'on achète et ce que l'on vend ; les anciennes adresses y redirigent.
const MesTransactionsPage = lazyRetry(() => import("./pages/transactions/MesTransactionsPage"));
const MonCodeRencontrePage = lazyRetry(() => import("./pages/compte/MonCodeRencontrePage"));
const VersTransactions    = lazyRetry(() => import("./pages/transactions/MesTransactionsPage").then((m) => ({ default: m.VersTransactions })));
const VentesOuTransactions = lazyRetry(() => import("./pages/transactions/MesTransactionsPage").then((m) => ({ default: m.VentesOuTransactions })));
// Onboarding
const OnboardingPage    = lazyRetry(() => import("./pages/auth/OnboardingPage"));
// Gamification
const LeaderboardPage   = lazyRetry(() => import("./pages/gamification/LeaderboardPage"));
// Payment
const PaymentSuccessPage = lazyRetry(() => import("./components/PaymentComponents").then(m => ({ default: m.PaymentSuccessPage })));
const StripeConnectLabPage = lazyRetry(() => import("./pages/internal/StripeConnectLabPage"));
// PurchaseRequestsPage supprimé — flow gated-buy retiré avec le pivot PayPal.

// ─── Spinner de chargement ────────────────────────────────────────────────────
const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1DA1F2]" />
  </div>
);

// ─── Error Boundary ───────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null, componentStack: "" }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, errorInfo) {
    // Log to console for debugging
    console.error("[ErrorBoundary] React render crash:", error?.message || error);
    console.error("[ErrorBoundary] Component stack:", errorInfo?.componentStack);
    // Diagnostic transitoire — on conserve le componentStack pour l'afficher à
    // l'écran (cf. render), le temps de localiser un crash de prod reproductible
    // uniquement côté client. À retirer une fois la cause corrigée.
    try { this.setState({ componentStack: String(errorInfo?.componentStack || "").slice(0, 600) }); } catch (_) {}
    // Send to Sentry if available
    // Sentry différé (utils/sentryDiffere) : envoyé tout de suite s'il est chargé,
    // sinon dès son chargement — un crash du démarrage n'est pas perdu.
    avecSentry((S) => S.captureException(error, { extra: { componentStack: errorInfo?.componentStack } }));
    // Beacon serveur (corrélation Railway/request_id) en plus de Sentry.
    try {
      window.__giveReportError__ && window.__giveReportError__({
        event: "REACT_ERROR_BOUNDARY",
        message: error?.message || String(error || ""),
        stack: error?.stack || "",
        extra: { componentStack: String(errorInfo?.componentStack || "").slice(0, 1000) },
      });
    } catch (_) { /* never break the fallback UI */ }
  }
  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }
  render() {
    if (this.state.hasError) return (
      <div className="min-h-screen flex items-center justify-center p-4 text-center">
        <div>
          <p className="text-3xl mb-3">😵</p>
          <p className="text-slate-600 mb-4">Une erreur inattendue est survenue</p>
          {/* Détail technique affiché UNIQUEMENT en développement (jamais en
              production : un utilisateur ne doit pas voir de stack trace). */}
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre className="text-[10px] text-left text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-4 max-w-md mx-auto whitespace-pre-wrap break-all overflow-auto" style={{ maxHeight: 220 }}>
              {String(this.state.error.message || this.state.error)}
              {this.state.componentStack ? "\n--- composant ---" + this.state.componentStack : ""}
            </pre>
          )}
          <button onClick={() => window.location.reload()} className="btn-primary px-6 py-3">
            Recharger la page
          </button>
        </div>
      </div>
    );
    return this.props.children;
  }
}

const ErrorBoundaryWithReset = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
};

/**
 * SilentErrorBoundary — for non-critical top-level widgets (PWA prompt,
 * cookie banner). If they crash (e.g. iOS Safari Private Browsing blocks
 * localStorage), we render nothing instead of a full-screen error UI
 * that would cover the whole app.
 */
class SilentErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, errorInfo) {
    console.warn("[SilentErrorBoundary] widget crashed, hiding:", error?.message);
  }
  render() { return this.state.hasError ? null : this.props.children; }
}

// ─── Auth Callback (Google OAuth) ─────────────────────────────────────────────
const AuthCallback = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const run = async () => {
      const qs = new URLSearchParams(window.location.search);
      const code = qs.get("code");
      const state = qs.get("state");
      if (!code) { navigate("/login", { replace: true }); return; }
      try {
        // #2378 — l'intention « CGU/CGV acceptées » cochée avant le bouton a
        // survécu à l'aller-retour Google dans sessionStorage : on la relit UNE
        // fois et on la joint à l'échange. Le serveur journalise l'acte.
        const accept_terms = lireConsentementOauth();
        const { data } = await api.post("/auth/google/callback", { code, state, accept_terms });
        // Le cookie httpOnly est set par le backend
        await checkAuth();
        toast.success(`Bienvenue ${data?.user?.name || ""} !`);
        window.history.replaceState(null, "", window.location.pathname);
        let _rt = "/profile";
        try {
          const _s = sessionStorage.getItem("gively_return_to");
          if (_s && _s.startsWith("/") && !_s.startsWith("//")) { _rt = _s; sessionStorage.removeItem("gively_return_to"); }
        } catch (_) { /* noop */ }
        window.location.href = _rt;
      } catch {
        toast.error("Erreur d'authentification");
        navigate("/login", { replace: true });
      }
    };
    run();
  }, [navigate, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#1DA1F2] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-500">Connexion en cours...</p>
      </div>
    </div>
  );
};

// ─── Protected Route ─────────────────────────────────────────────────────────
const ProtectedRoute = ({ children, user, loading, allowAsso = false }) => {
  // allowAsso : autorise aussi un compte association connecté (sans
  // session user normale) à accéder à la route. Sert pour le tunnel de
  // publication commun /publish, qu'une asso emprunte exactement comme
  // un user (cf. NewPublishPage → isAssoPublishing). Sans ça, une asso
  // (qui n'a pas de `user`) était redirigée vers /login.
  const { account: assoAccount, loading: assoLoading } = useAssociationAuth() || {};
  if (loading || (allowAsso && assoLoading)) {
    // Auth check in progress — show spinner, don't redirect
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-3 border-[#1DA1F2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) {
    return children;
  }
  if (allowAsso && assoAccount) {
    return children;
  }
  // Chantier Acquisition (finding P0-1) : mémoriser la page visée pour y
  // REVENIR après connexion — avant, l'intention (annonce, tunnel publish)
  // était détruite : login → /profile. sessionStorage plutôt que state :
  // survit au reload complet (window.location.replace) et à l'OAuth.
  try {
    const _dest = window.location.pathname + window.location.search;
    if (_dest && _dest !== "/login") sessionStorage.setItem("gively_return_to", _dest);
    // 10/10/2026 — lot E1 : le mur d'une page réservée se compte aussi (une
    // balise par page visée, pas une par rendu).
    if (window.__givelyDernierMur !== window.location.pathname) {
      window.__givelyDernierMur = window.location.pathname;
      signalerMurConnexion("page_protegee");
    }
  } catch (_) { /* noop */ }
  return <Navigate to="/login" replace />;
};

// 02/11/2026 — publier SANS compte (Lucas : « tout ce que tu me proposes »). Logs du
// 27/10 au 02/11 : 11 visiteurs bloqués par le mur de connexion à l'entrée de
// /publish, avant d'avoir rien saisi. Le tunnel (création seulement) est désormais
// ouvert : le brouillon et les photos restent sur l'appareil, et le compte n'est
// demandé qu'au clic « Publier » (PublishWizard), avec retour automatique ici.
// L'ancien tunnel (flag PHOENIX coupé) garde la connexion obligatoire.
const PublishRoute = ({ user, loading }) => {
  const { account: assoAccount, loading: assoLoading } = useAssociationAuth() || {};
  if (loading || assoLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-3 border-[#1DA1F2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user || assoAccount || isPhoenixPublishEnabled()) return <PublishEntry user={user} />;
  return <ProtectedRoute user={user} loading={loading} allowAsso><PublishEntry user={user} /></ProtectedRoute>;
};

// Redirection de l'ancienne route d'édition (EditListingPage, tunnel divergent
// qui perdait type/calendrier/colis/fiscal) vers le VRAI tunnel d'édition unifié
// /publish/:id/edit. Zéro doublon : un seul tunnel de création/édition.
const LegacyListingEditRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/publish/${id}/edit`} replace />;
};

// ─── Router ───────────────────────────────────────────────────────────────────
function AppRouter() {
  const { user, loading, logout, legalStatus, refreshLegalStatus, checkAuth } = useAuth();
  // Le mur juridique vaut pour les DEUX types de compte : une association
  // publie et achete par le meme tunnel qu'un utilisateur, et la garde
  // serveur est bi-mode (`dependencies.py:596`).
  const {
    account: assoAccountLegal,
    legalStatus: assoLegalStatus,
    refreshLegalStatus: refreshAssoLegalStatus,
    checkAuth: checkAssoAuth,
  } = useAssociationAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const noHeader = ["/admin", "/association/login", "/association/register", "/association/setup-password", "/association/reset-password"];
  const showHeader = !noHeader.some(p => location.pathname.startsWith(p));

  // Gate de vérification email : un utilisateur dont l'email n'est PAS vérifié
  // (email_verified === false, posé à l'inscription par email ; absent pour les
  // comptes Google/legacy → non bloqués) est ramené sur /verify-email tant qu'il
  // n'a pas validé. On exempte les pages d'auth et les espaces admin/asso.
  useEffect(() => {
    if (loading || !user) return;
    if (user.email_verified !== false) return;
    const p = location.pathname;
    // Chantier Acquisition (finding P0-3) : la CONSULTATION reste libre pour
    // un compte non vérifié (email en spam = utilisateur enfermé dehors dès
    // la 1ʳᵉ minute). Seules les routes d'ACTION (publier, acheter, messages,
    // profil…) restent gatées — elles ne sont pas listées ici.
    const exempt =
      p === "/" ||
      p.startsWith("/verify-email") || p.startsWith("/login") || p.startsWith("/register") ||
      p.startsWith("/forgot") || p.startsWith("/reset") || p.startsWith("/auth/") ||
      p.startsWith("/admin") || p.startsWith("/association") || p.startsWith("/legal") ||
      p.startsWith("/cgu") || p.startsWith("/cgv") || p.startsWith("/confidentialite") ||
      p.startsWith("/search") || p.startsWith("/listing") || p.startsWith("/categorie") ||
      p.startsWith("/blog") || p.startsWith("/faq") || p.startsWith("/comment-");
    if (!exempt) {
      navigate("/verify-email", { replace: true });
    }
  }, [user, loading, location.pathname, navigate]);

  // Phase 9 — bfcache iOS Safari : quand l'utilisateur revient en
  // arrière depuis PayPal / Apple Pay / Google Pay (ou simplement
  // tape "back"), iOS Safari restore la page depuis le bfcache
  // (back/forward cache) sans rejouer les useEffect, donc tous les
  // états React (auth, panier, drafts, statut de paiement) restent
  // figés sur leur snapshot d'avant la sortie. `event.persisted ===
  // true` signale ce cas précis. On force un reload pour repartir
  // d'un état frais et éviter qu'un user voie son panier vide ou
  // un statut "paiement en cours" alors que le paiement est validé.
  // Cf. https://web.dev/articles/bfcache + WebKit bug bugs.webkit.org/show_bug.cgi?id=211018
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) {
        // eslint-disable-next-line no-console
        console.log("[BFCACHE] page restored from bfcache → reload");
        window.location.reload();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  // Attribution association — capture le lien de partage `?asso=<ID>` au
  // chargement initial (clic d'un adhérent sur le lien de son asso) et le
  // persiste 30j, pour pré-sélectionner l'asso à la publication même si
  // l'adhérent explore le site avant de vendre.
  useEffect(() => {
    captureAssoReferralFromUrl();
  }, []);

  // Remonte en haut de page à chaque changement de route. Sans ça, React
  // Router conserve la position de scroll de la page précédente : on
  // arrivait parfois au milieu ou en bas d'une nouvelle page. On ignore
  // les liens ancres (#hash) qui doivent scroller vers leur cible.
  //
  // RETOUR = MÊME ENDROIT (30/09/2026, Lucas : « je reviens tout en haut de mon
  // espace association, c'est chiant »). Sur un RETOUR (navigation POP : bouton
  // retour du navigateur, du téléphone, ou `navigate(-1)` de nos boutons
  // « Retour »), on restaure la position mémorisée pour cette entrée
  // d'historique au lieu de remonter. Une NOUVELLE page part toujours du haut.
  // Un changement de paramètres sur la même page (filtres de recherche) ne
  // bouge rien, comme avant.
  const navigationType = useNavigationType();
  const cheminPrecedent = useRef(location.pathname);
  useEffect(() => {
    const cle = location.key;
    let attente = null;
    const surDefilement = () => {
      if (attente) return;
      attente = requestAnimationFrame(() => {
        attente = null;
        // L'entrée a déjà changé (clic sur un lien) : la position lue est
        // celle de la page suivante, on ne l'attribue pas à celle-ci.
        if (window.history.state?.key && window.history.state.key !== cle) return;
        memoriserPosition(cle, window.scrollY);
      });
    };
    window.addEventListener("scroll", surDefilement, { passive: true });
    return () => {
      window.removeEventListener("scroll", surDefilement);
      if (attente) cancelAnimationFrame(attente);
    };
  }, [location.key]);
  useEffect(() => {
    const cheminChange = cheminPrecedent.current !== location.pathname;
    cheminPrecedent.current = location.pathname;
    if (location.hash) return undefined;
    if (navigationType === "POP") {
      const y = positionMemorisee(location.key);
      if (y != null) return restaurerPosition(y);
    }
    if (cheminChange) window.scrollTo(0, 0);
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- volontaire : une réaction par ENTRÉE d'historique ; hash et chemin sont lus, pas déclencheurs
  }, [location.key]);

  // ── MUR JURIDIQUE : deux sources, un seul overlay ────────────────────────
  // 1) PROACTIVE — le statut lu a la connexion dit que les documents ont
  //    change. On previent AVANT que l'utilisateur se heurte a quoi que ce
  //    soit. Existe pour l'utilisateur depuis toujours ; ajoute ici pour
  //    l'ASSOCIATION, qui n'avait aucun moyen de l'apprendre (LOT 1b).
  // 2) REACTIVE — le serveur vient de refuser une action en 451. C'est le
  //    filet : il attrape le cas ou le statut proactif n'a pas ete lu (onglet
  //    ouvert depuis longtemps, bundle mobile ancien, appel echoue en silence).
  //
  // La source REACTIVE prime : elle decrit un refus qui vient d'arriver, donc
  // l'etat le plus frais que le serveur ait exprime.
  const [legalGate, setLegalGate] = useState(null);
  useEffect(() => onLegalGate(setLegalGate), []);
  // Fenêtre de grâce (#2377) : avant la date d'entrée en vigueur, un statut en
  // retard n'est plus un MUR mais un BANDEAU. Si l'utilisateur clique « Lire et
  // accepter » dessus, on monte la modale VOLONTAIREMENT — elle est alors
  // refermable (« Plus tard »). Le serveur reste seul juge du blocage.
  const [graceOuverte, setGraceOuverte] = useState(false);

  const { mur, grace } = etatMurJuridique({
    user, legalStatus, assoAccount: assoAccountLegal, assoLegalStatus, legalGate,
  });
  // IMPASSE FERMÉE (23/09/2026) — le mur ne se monte JAMAIS par-dessus le
  // document qu'il demande d'accepter. Sans cette ligne, ouvrir « CGV » depuis
  // la modale rechargeait l'application dans un nouvel onglet et la MÊME modale
  // se rabattait sur le texte : il fallait accepter pour lire, et lire pour
  // accepter. Signalé par la présidente d'ABC MucO, qui n'a pas pu adhérer.
  //
  // Ce n'est pas un trou dans le mur : ces quatre pages sont en LECTURE SEULE,
  // on n'y publie pas, on n'y encaisse pas. Le mur garde tout le reste, et le
  // serveur reste seul juge (451) sur les actions. Ce qu'on retire ici, c'est
  // uniquement l'obstacle entre quelqu'un et le contrat qu'on lui fait signer.
  const surPageDocument = estPageDocumentJuridique(location.pathname);
  const murJuridique = murAAfficher({ mur, grace, graceOuverte, pathname: location.pathname });
  const murEstAsso = murJuridique?.accountType === "association";

  return (
    <>
      {/* Bandeau NON bloquant — fenêtre de grâce avant la date d'entrée en vigueur (#2377) */}
      {grace && !murJuridique && !surPageDocument && (
        <LegalGraceBanner
          effectiveAt={grace.effectiveAt}
          isAssociation={grace.accountType === "association"}
          onOpen={() => setGraceOuverte(true)}
        />
      )}
      {/* Paramètres → Affichage : « réduire les animations » suit le compte (05/11/2026). */}
      <SynchroAffichage />
      {/* Overlay bloquant — documents juridiques a re-accepter (user OU asso) */}
      {murJuridique && (
        <LegalUpdateModal
          needsAcceptance={murJuridique.needsAcceptance}
          isAssociation={murEstAsso}
          effectiveAt={murJuridique.volontaire ? murJuridique.effectiveAt : undefined}
          onDismiss={murJuridique.volontaire ? () => setGraceOuverte(false) : undefined}
          // Porte de sortie du mur OBLIGATOIRE : se déconnecter n'accepte rien
          // et ne lève pas le mur — il rend simplement sa liberté à qui refuse.
          onLogout={logout}
          // Les deux points d'acceptation sont DISTINCTS. Poster au mauvais
          // endroit renverrait un 401 avale par le `catch` de la modale : la
          // modale se refermerait sans que rien ne soit enregistre, et le mur
          // reviendrait a l'action suivante, sans explication.
          acceptEndpoint={murEstAsso ? "/associations/auth/accept-legal" : "/auth/accept-legal"}
          onAccepted={() => {
            // On efface d'abord le refus REACTIF : sans ca, la modale resterait
            // montee sur un 451 desormais perime, meme apres acceptation.
            setLegalGate(null);
            setGraceOuverte(false);
            if (murEstAsso) {
              refreshAssoLegalStatus?.();
              checkAssoAuth?.();
            } else {
              refreshLegalStatus();
              checkAuth();
            }
          }}
        />
      )}
      {showHeader && <DesktopHeader />}
      <Suspense fallback={<Spinner />}>
        <Routes>
          {/* Auth */}
          <Route path="/auth/google/callback" element={<AuthCallback />} />
          <Route path="/login"           element={<LoginPage />} />
          <Route path="/register"        element={<RegisterPage />} />
          <Route path="/verify-email"    element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password"  element={<ResetPasswordPage />} />
          {/* Main */}
          <Route path="/"         element={<HomePage />} />
          <Route path="/share-target" element={<ShareTargetPage />} />
          <Route path="/open-file"    element={<OpenFilePage />} />
          <Route path="/search"   element={<SearchPage />} />
          {/* PHOENIX — /publish passe par PublishEntry : bascule ancien tunnel /
              nouveau wizard selon le flag REACT_APP_PUBLISH_PHOENIX (coexistence +
              rollback instantané). Couvre USER, ASSOCIATION et ÉDITION. */}
          <Route path="/publish"  element={<PublishRoute user={user} loading={loading} />} />
          {/* Phase 3 — Édition annonce via le MÊME tunnel que la publication
              (création et édition partagent le moteur). En mode édition, le
              wizard PHOENIX charge l'annonce (GET → listingToFormData → REPLACE)
              puis PUT au submit ; l'ancien tunnel détecte useParams().listingId. */}
          <Route path="/publish/:listingId/edit" element={<ProtectedRoute user={user} loading={loading} allowAsso><PublishEntry user={user} /></ProtectedRoute>} />
          {/* PHOENIX — route de PRÉVISUALISATION du nouveau tunnel refondu.
              Séparée de /publish (qui reste l'ancien tunnel inchangé en prod) :
              zéro risque utilisateur, validation mobile au fur et à mesure.
              Les étapes non encore reconstruites sont des marque-places ; aucune
              publication réelle n'est déclenchée depuis cette preview. */}
          <Route path="/publish-phoenix" element={<ProtectedRoute user={user} loading={loading} allowAsso><PublishWizard user={user} /></ProtectedRoute>} />
          {/* Listings */}
          <Route path="/listing/:id/edit" element={<LegacyListingEditRedirect />} />
          <Route path="/listing/:id/choose-asso" element={<ProtectedRoute user={user} loading={loading}><ChooseAssoPage /></ProtectedRoute>} />
          <Route path="/listing/:listingId/boost" element={<ProtectedRoute user={user} loading={loading} allowAsso><BoostPage /></ProtectedRoute>} />
          <Route path="/listing/:listingId/boost/stripe" element={<ProtectedRoute user={user} loading={loading} allowAsso><BoostStripePage /></ProtectedRoute>} />
          {/* Pas de allowAsso : une association ne peut pas ACHETER. */}
          <Route path="/payment/stripe-purchase/:listingId" element={<ProtectedRoute user={user} loading={loading}><StripePurchasePage /></ProtectedRoute>} />
          {/* Ancienne route PayPal (mode = boost | purchase) : redirigée vers le
              rail Stripe. Voir LegacyPaypalPaymentRedirect ci-dessus. */}
          <Route path="/payment/:mode/:resourceId" element={<ProtectedRoute user={user} loading={loading}><LegacyPaypalPaymentRedirect /></ProtectedRoute>} />
          <Route path="/listing/:slug"    element={<ListingDetailPage />} />
          <Route path="/checkout/:listingId" element={<ProtectedRoute user={user} loading={loading}><CheckoutPage /></ProtectedRoute>} />
          {/* Associations */}
          <Route path="/associations"   element={<AssociationsPage />} />

          {/* Espace association — routes specifiques AVANT le catch-all :id */}
          <Route path="/association/devenir-partenaire" element={<AssociationLandingPage />} />
          <Route path="/association/login"    element={<AssociationLoginPage />} />
          {/* 27/09/2026 — deux lettres Resend déjà envoyées pointaient cette
              adresse, qui n'a jamais existé (page 404). On la fait mener à
              l'espace association ; le lien des lettres est corrigé côté serveur. */}
          <Route path="/association-espace" element={<Navigate to="/association/dashboard" replace />} />
          <Route path="/association/register" element={<AssociationRegisterPage />} />
          <Route path="/association/setup-password" element={<AssociationSetupPasswordPage />} />
          <Route path="/association/reset-password" element={<AssociationResetPasswordPage />} />
          <Route path="/association/dashboard" element={<AssociationDashboardPage />} />
          <Route path="/association/ad-grants" element={<AssociationAdGrantsPage />} />
          <Route path="/association/affichage-libre" element={<AssociationAffichageLibrePage />} />
          <Route path="/association/verification" element={<Navigate to="/association/dashboard?tab=wallet" replace />} />
          <Route path="/association/cgu"       element={<AssociationCGUPage />} />
          <Route path="/association/charte"    element={<AssociationChartePage />} />
          <Route path="/association/:slug" element={<AssociationDetailPage />} />
          {/* /annuaire (RNADirectoryPage) removed with the RNA sunset */}
          <Route path="/annuaire"        element={<Navigate to="/associations" replace />} />
          {/* Blog — public */}
          <Route path="/blog"            element={<BlogListPage />} />
          <Route path="/blog/:slug"      element={<BlogDetailPage />} />
          {/* Dashboard user */}
          <Route path="/dashboard"      element={<Navigate to="/profile" replace />} />
          <Route path="/favorites"      element={<ProtectedRoute user={user} loading={loading}><FavoritesPage user={user} /></ProtectedRoute>} />
          {/* allowAsso — parité User↔Association : le centre de notifications est
              multi-acteur côté backend (notifications.py:32 get_current_user_or_association,
              scope strict par _notif_owner_ids → aucune fuite inter-acteur). Sans allowAsso,
              une association était redirigée vers /login alors que l'API la sert déjà.
              NotificationsPage lit via le client `api` (cookie de l'acteur) et ne déréférence
              pas `user` → sûr pour une asso (user=null). */}
          <Route path="/notifications"  element={<ProtectedRoute user={user} loading={loading} allowAsso><NotificationsPage user={user} /></ProtectedRoute>} />
          <Route path="/mes-recherches" element={<ProtectedRoute user={user} loading={loading}><SavedSearchesPage user={user} /></ProtectedRoute>} />
          <Route path="/mes-transactions" element={<ProtectedRoute user={user} loading={loading}><MesTransactionsPage /></ProtectedRoute>} />
          {/* Code de rencontre tournant (25/10/2026) : particuliers ET associations. */}
          <Route path="/mon-code-rencontre" element={<ProtectedRoute user={user} loading={loading} allowAsso><MonCodeRencontrePage /></ProtectedRoute>} />
          <Route path="/mes-achats"     element={<ProtectedRoute user={user} loading={loading}><VersTransactions vue="achete" type="bien" /></ProtectedRoute>} />
          <Route path="/mes-boosts"     element={<ProtectedRoute user={user} loading={loading}><PurchaseHistoryPage mode="boost" /></ProtectedRoute>} />
          {/* Phase 27D3.2 — deep link confirmation acheteur depuis l'email
              "Avez-vous bien récupéré votre article ?". Route protégée
              car POST /api/delivery/confirm-receipt requiert un cookie
              de session. */}
          <Route path="/confirm-receipt" element={<ProtectedRoute user={user} loading={loading}><ConfirmReceiptPage /></ProtectedRoute>} />
          {/* UNIFICATION ventes : allowAsso → une asso accède à SES ventes
              et au bouton « Générer l'étiquette » (même page que les users). */}
          <Route path="/mes-ventes"    element={<ProtectedRoute user={user} loading={loading} allowAsso><VentesOuTransactions /></ProtectedRoute>} />
          <Route path="/mes-annonces"  element={<ProtectedRoute user={user} loading={loading}><MyListingsPage /></ProtectedRoute>} />
          <Route path="/mes-dons"       element={<ProtectedRoute user={user} loading={loading}><MyDonationsPage /></ProtectedRoute>} />
          {/* Réclamations — commune à tous les comptes connectés : user,
              pro (account_type='professional') et asso (allowAsso). */}
          <Route path="/reclamations"  element={<ProtectedRoute user={user} loading={loading} allowAsso><ReclamationsPage /></ProtectedRoute>} />
          <Route path="/mes-locations"  element={<ProtectedRoute user={user} loading={loading}><VersTransactions type="location" /></ProtectedRoute>} />
          <Route path="/mes-services"   element={<ProtectedRoute user={user} loading={loading}><VersTransactions type="service" /></ProtectedRoute>} />
          <Route path="/mes-offres"     element={<ProtectedRoute user={user} loading={loading}><VersTransactions /></ProtectedRoute>} />
          {/* /revenus retiré du menu — legacy EarningsPage lit payment_transactions
              (Stripe) et n'est pas branché sur le flux PayPal MVP actuel.
              Redirection vers /mes-ventes qui est la source de vérité. */}
          <Route path="/revenus"       element={<Navigate to="/mes-ventes" replace />} />
          {/* Profile */}
          <Route path="/profile"      element={<ProtectedRoute user={user} loading={loading}><ProfilePage /></ProtectedRoute>} />
          <Route path="/profile/edit" element={<ProtectedRoute user={user} loading={loading}><EditProfilePage /></ProtectedRoute>} />
          <Route path="/user/:userId" element={<PublicProfilePage currentUser={user} />} />
          {/* /versements : adresse unique des « Site links » Stripe (28/09/2026) —
              aiguille association → onglet Versements, utilisateur → /wallet. */}
          <Route path="/versements" element={<VersementsAiguillage user={user} loading={loading} />} />
          <Route path="/wallet" element={<ProtectedRoute user={user} loading={loading}><WalletPage /></ProtectedRoute>} />
          {/* F4 (espace vendeur 2026-07-12) : /portefeuille = alias parlant de la
              page Versements enrichie (solde, versements, créances, relevé par
              vente, exports, PDF, dons) — même composant, AUCUNE page dupliquée. */}
          <Route path="/portefeuille" element={<Navigate to="/wallet" replace />} />
          {/* Phase 5+ — Page gamification dédiée. */}
          <Route path="/mon-impact" element={<ProtectedRoute user={user} loading={loading}><MyImpactPage /></ProtectedRoute>} />
          {/* Legacy Stripe redirects — keep soft redirect so old links don't 404. */}
          {/* Lab interne Stripe Connect embedded (non public, noindex, gardé admin
              côté backend). Inerte tant que les clés Stripe ne sont pas en env. */}
          <Route path="/internal/stripe-connect-lab" element={<ProtectedRoute user={user} loading={loading}><StripeConnectLabPage /></ProtectedRoute>} />
          <Route path="/stripe" element={<Navigate to="/wallet" replace />} />
          <Route path="/verification" element={<Navigate to="/wallet" replace />} />
          <Route path="/connect/return"  element={<Navigate to="/wallet" replace />} />
          <Route path="/connect/refresh" element={<Navigate to="/wallet" replace />} />
          {/* Purchase requests UX (Bloc 5): single page that shows
              both incoming (seller) and outgoing (buyer) requests.
              Deep-link by id is also accepted and resolves into the
              same page (frontend reads the param to highlight). */}
          {/* /mes-demandes retiré — flow gated-buy (purchase_requests) supprimé. */}
          <Route path="/mes-demandes" element={<Navigate to="/mes-achats" replace />} />
          <Route path="/mes-demandes/:requestId" element={<Navigate to="/mes-achats" replace />} />
          <Route path="/onboarding" element={<ProtectedRoute user={user} loading={loading}><OnboardingPage /></ProtectedRoute>} />
          <Route path="/simulation" element={<ProtectedRoute user={user} loading={loading}><SimulationPage /></ProtectedRoute>} />
          {/* Paramètres (05/11/2026) : écrans partagés avec l'espace association
              (pages/parametres). allowAsso : une association qui arrive par un
              lien profond (/settings/notifications) voit les mêmes écrans. */}
          <Route path="/settings"     element={<ProtectedRoute user={user} loading={loading} allowAsso><SettingsPage /></ProtectedRoute>} />
          <Route path="/settings/:section" element={<ProtectedRoute user={user} loading={loading} allowAsso><SettingsPage /></ProtectedRoute>} />
          {/* Centre de préférences email — PUBLIC (token HMAC du pied d'email,
              sans login) OU connecté (session). Noindex. */}
          <Route path="/preferences-email" element={<PreferencesEmailPage />} />
          {/* Messages */}
          {/* UNIFICATION messagerie : allowAsso → une association utilise la
              MÊME messagerie que les users (MessagesPage résout l'acteur). */}
          <Route path="/messages"                element={<ProtectedRoute user={user} loading={loading} allowAsso><MessagesPage /></ProtectedRoute>} />
          <Route path="/messages/:conversationId" element={<ProtectedRoute user={user} loading={loading} allowAsso><ConversationPage /></ProtectedRoute>} />
          {/* Gamification */}
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          {/* Admin V2 — nested routes with sidebar layout.
              directory / prospection / enrichment / imports routes removed
              with the RNA sunset; they now redirect to /admin/follow-up. */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardV2 />} />
            <Route path="associations" element={<AdminAssociationsV2 />} />
            <Route path="campaigns" element={<AdminCampaignV2 />} />
            <Route path="follow-up" element={<AdminFollowUpV2 />} />
            <Route path="blog" element={<AdminBlogV2 />} />
            <Route path="app-store" element={<AdminAppStoreAssets />} />
            <Route path="directory" element={<Navigate to="/admin/follow-up" replace />} />
            <Route path="prospection" element={<Navigate to="/admin/follow-up" replace />} />
            <Route path="enrichment" element={<Navigate to="/admin/campaigns" replace />} />
            <Route path="imports" element={<Navigate to="/admin/campaigns" replace />} />
            <Route path="users" element={<AdminUsersV2 />} />
            <Route path="listings" element={<AdminListingsV2 />} />
            <Route path="reports" element={<AdminReportsV2 />} />
            <Route path="disputes" element={<AdminDisputesV2 />} />
            <Route path="deposits" element={<AdminDepositsV2 />} />
            <Route path="messaging" element={<AdminMessagingV2 />} />
            <Route path="kpi" element={<AdminKpiV2 />} />
            <Route path="coherence" element={<AdminCoherenceV2 />} />
            {/* Santé de Gively — voyants par sous-système (Lucas 2026-07-14). */}
            <Route path="sante" element={<AdminPlatformHealth />} />
            {/* Observation lancement (J+30) — activité réelle sur fenêtre glissante (Lucas 2026-07-14). */}
            <Route path="observation" element={<AdminLaunchMetrics />} />
            <Route path="documents" element={<AdminDocumentsV2 />} />
            <Route path="emails" element={<AdminEmailsV2 />} />
            {/* Phase 28 — consolidation : une SEULE page de paiement admin =
                « Transactions ». Archivage 24/08/2026 : les filets hors menu
                payouts-legacy et sales (liste) sont supprimés (zéro lien
                entrant, redondants) ; les anciens liens /admin/sales et
                /admin/payouts retombent sur Transactions. Le détail
                sales/:id RESTE (utilisé par Transactions et Signalements). */}
            <Route path="payouts" element={<Navigate to="/admin/transactions" replace />} />
            <Route path="sales" element={<Navigate to="/admin/transactions" replace />} />
            <Route path="sales/:id" element={<AdminSaleDetailV2 />} />
            <Route path="alerts" element={<AdminAlertsV2 />} />
            <Route path="blocked-cases" element={<AdminBlockedCasesV2 />} />
            <Route path="production-watch" element={<AdminProductionWatch />} />
            <Route path="boxtal" element={<AdminBoxtalV2 />} />
            <Route path="outils" element={<AdminToolsV2 />} />
            {/* Phase 6+ — Tableau unifié ventes + boosts. */}
            <Route path="transactions" element={<AdminTransactionsV2 />} />
            {/* Finance — hub : liens Dashboard Stripe + écrans Gively-uniques. */}
            <Route path="finance" element={<AdminFinanceHub />} />
            {/* Finance — Simulation Stripe (04/10/2026) : remplace l'ancien simulateur de marge PayPal. */}
            <Route path="finance/simulation-stripe" element={<AdminStripeSimulation />} />
            {/* Comptabilité / simulateur de marge nette (admin only). */}
            <Route path="accounting" element={<AdminAccountingV2 />} />
            {/* DAC7 — assistant déclaration annuelle des revenus vendeurs. */}
            <Route path="dac7" element={<AdminDac7V2 />} />
            {/* Phase 8.22 — UI scraper assos (SIRENE + DDG + email). */}
            <Route path="asso-prospect" element={<AdminAssoProspect />} />
            <Route path="federations" element={<AdminFederations />} />
            <Route path="commercants" element={<AdminCommercants />} />
            <Route path="acquisition" element={<AdminAcquisition />} />
            <Route path="referencement" element={<AdminReferencement />} />
            <Route path="press" element={<AdminPress />} />
            {/* PayPal MVP — anciens onglets Stripe Connect / manual-payouts supprimés.
                Phase 28 — redirigés vers la page unique « Transactions ». */}
            <Route path="manual-payouts" element={<Navigate to="/admin/transactions" replace />} />
            <Route path="connect" element={<Navigate to="/admin/transactions" replace />} />
          </Route>
          {/* SEO Landing Pages */}
          <Route path="/pour-les-associations" element={<PourLesAssociationsPage />} />
          <Route path="/commercants" element={<CommercantsLandingPage />} />
          <Route path="/comment-financer-une-association" element={<CommentFinancerAssociationPage />} />
          <Route path="/comment-financer-un-club-de-sport" element={<CommentFinancerClubSportPage />} />
          <Route path="/comment-financer-un-projet-scolaire" element={<CommentFinancerProjetScolairePage />} />
          <Route path="/comment-financer-une-association-etudiante" element={<CommentFinancerAssociationEtudiantePage />} />
          <Route path="/comment-organiser-une-collecte-de-fonds" element={<OrganiserCollecteFondsPage />} />
          <Route path="/comment-vendre-en-toute-securite" element={<CommentVendreSecuritePage />} />
          <Route path="/aide/quand-suis-je-paye" element={<ArgentVentesPage />} />
          <Route path="/comment-fonctionne-un-recu-fiscal" element={<RecuFiscalPage />} />
          <Route path="/assos-fondatrices" element={<AssosFondatricesPage />} />
          <Route path="/associations/rejoindre" element={<AssociationJoinPage />} />
          <Route path="/marketplace-solidaire" element={<MarketplaceSolidairePage />} />
          <Route path="/vendre-avec-don" element={<VendreAvecDonPage />} />
          <Route path="/louer-entre-particuliers" element={<LouerEntreParticuliersPage />} />
          <Route path="/services-solidaires" element={<ServicesSolidairesPage />} />
          <Route path="/c-est-quoi-une-marketplace-solidaire" element={<MarketplaceSolidaireFAQPage />} />
          <Route path="/recherche/:slug" element={<SearchLandingPage />} />
          <Route path="/revente-solidaire" element={<ReventeSolidairePage />} />
          {/* 02/11/2026 — fusionnée dans /marketplace-solidaire (301 serveur, pages-disparues.js). */}
          <Route path="/marketplace-solidaire-france" element={<Navigate to="/marketplace-solidaire" replace />} />
          <Route path="/financer-son-association" element={<FinancerAssociationPage />} />
          {/* Phase 8.17 — /collecter-des-dons : 301 vers /financer-son-association
              pour eviter duplicate-content Google. Le contenu etait identique
              car les 2 routes pointaient sur le meme composant. */}
          <Route path="/collecter-des-dons" element={<Navigate to="/financer-son-association" replace />} />
          <Route path="/consommer-responsable" element={<ConsommerResponsablePage />} />

          {/* Phase 12.3 — Programmatic SEO. */}
          <Route path="/associations/departement/:code" element={<DepartmentAssociationsPage />} />
          <Route path="/associations/cause/:cause" element={<CauseAssociationsPage />} />
          <Route path="/site-de-vente-pour-association" element={<Navigate to="/vendre-avec-don" replace />} />
          <Route path="/collecter-fonds-association" element={<CollecterFondsAssociationPage />} />
          <Route path="/vente-objet-don" element={<VenteObjetDonPage />} />
          <Route path="/vide-grenier-solidaire" element={<VideGrenierSolidairePage />} />

          {/* Phase 11.7 — Suppression des redirects /alternative-* sur
              demande Lucas (mai 2026). Lucas ne veut aucune mention de
              marque concurrente dans le codebase. Les URLs sont retirées
              du SPA et de _redirects ; elles renverront un 404 et Google
              les désindexera dans les prochains crawls. Le contenu reste
              accessible aux URLs canoniques (/revente-solidaire et
              /marketplace-solidaire, où /marketplace-solidaire-france a été fusionnée
              le 02/11/2026). */}

          {/* Legal */}
          <Route path="/cgu"            element={<CGUPage />} />
          <Route path="/cgv"            element={<CGVPage />} />
          <Route path="/confidentialite" element={<PrivacyPage />} />
          <Route path="/informations-legales" element={<InformationsLegalesPage />} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/referencement-et-classement" element={<ReferencementClassementPage />} />
          <Route path="/contrat-partenariat" element={<ContratPartenariatPage />} />
          <Route path="/mandat-collecte" element={<MandatCollectePage />} />
          <Route path="/fiscalite-dons-associations" element={<FiscaliteDonsPage />} />
          {/* Suppression de compte — page publique (exigence Google Play) */}
          <Route path="/account/delete" element={<AccountDeletionPage />} />
          {/* Static */}
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/comment-ca-marche" element={<HowItWorksPage />} />
          <Route path="/contact"           element={<ContactPage />} />
          <Route path="/faq"              element={<FAQPage />} />
          <Route path="/aide"             element={<HelpPage />} />
          <Route path="/presse"            element={<PressPage />} />
          <Route path="/presse/communique" element={<PressReleasePage />} />
          {/* Phase 12.8 — Programmatic SEO categories : 11 cats + 64 subs + 8 alias = 83 URLs. */}
          {/* SEO catégorie × ville (longue traîne locale) : 11 cats × 40 villes.
              Route placée AVANT /:subSlug (segment "ville" littéral, 3 niveaux). */}
          <Route path="/louer/:slug/ville/:citySlug" element={<CityRentalPage />} />
          <Route path="/services/:typeSlug/ville/:citySlug" element={<CityServicePage />} />
          <Route path="/categorie/:slug/ville/:citySlug" element={<CityCategoryPage />} />
          <Route path="/categorie/:slug/:subSlug" element={<CategoryPage />} />
          <Route path="/categorie/:slug" element={<CategoryPage />} />
          {/* Phase 12.9 — Newsletter publique indexable. Editions = blog posts slug newsletter-* */}
          <Route path="/newsletter" element={<NewsletterPage />} />
          {/* Phase 12.10 — Page /qui-sommes-nous (E-E-A-T pour Google YMYL). */}
          <Route path="/qui-sommes-nous" element={<QuiSommesNousPage />} />
          <Route path="/partenaires"     element={<PartenairesPage />} />
          <Route path="/charte-ethique" element={<CharteEthiquePage />} />
          <Route path="/revendre-ses-cadeaux-de-noel" element={<RevendreCadeauxNoelPage />} />
          <Route path="/review/:token"     element={<ReviewPage />} />
          <Route path="/payment-success"   element={<PaymentSuccessPage />} />
          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      {/* Widget flottant « Un problème ? » du tunnel d'inscription association
          (Lot B onboarding 05/08/2026). Se conditionne lui-même au pathname :
          rendu nul partout hors du tunnel. */}
      <AssoFeedbackWidget />
    </>
  );
}

// ─── App racine ───────────────────────────────────────────────────────────────
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AssociationAuthProvider>
          <div className="min-h-screen bg-white flex flex-col">
            {/* Écran d'accueil animé (logo qui se déploie) — overlay non bloquant,
                1x/session, l'app se rend dessous (aucun impact SEO/LCP). */}
            <SplashScreen />
            {/* Phase 8.17 — WCAG 2.4.1 Bypass Blocks */}
            <a href="#main-content" className="skip-to-content">
              Aller au contenu principal
            </a>
            <ErrorBoundaryWithReset>
              {/* Hauteur d'un écran au minimum (Lighthouse du 19/10/2026) : sans elle, le
                  pied de page apparaissait en bas de l'écran pendant le chargement puis
                  sautait quand la liste arrivait (CLS 0,33 sur /associations).
                  `min-h-[100dvh]` et non `min-h-screen` : index.css réécrit cette classe en
                  `-webkit-fill-available`, que Chrome lit comme `stretch` — elle ne
                  réservait aucune hauteur (mesuré : <main> de 48 px, #2691). */}
              <main id="main-content" className="min-h-[100dvh]">
                <AppRouter />
              </main>
            </ErrorBoundaryWithReset>
            {/* Phase 8.16 — LegalFooter monte (etait expor­te mais
                jamais utilise -> tous les liens CGU/CGV/mentions/contact
                etaient invisibles. Risque LCEN article 6-III-1 : acces
                permanent obligatoire aux mentions legales). */}
            <LegalFooter />
            <BottomNavigation />
            <Toaster position="top-center" richColors />
            {/* Console de debug embarquée (bouton 🐞) — visible UNIQUEMENT par un
                ADMIN connecté (Lucas) ou avec ?debug=1 explicite. Jamais montrée
                à un utilisateur lambda (web comme app native). */}
            <DebugConsole />
            <OfflineBanner />
            {/* Pré-prompt contextuel notifications (soft prompt). Se gère seul :
                ne s'affiche que pour un utilisateur connecté, permission "default",
                non reporté récemment. Cause racine corrigée du « ça ne demande
                jamais l'autorisation ». */}
            <PushPermissionPrimer />
            {/* Amorçage natif (Capacitor) — no-op sur le web : deep links,
                push FCM, back button Android, status bar. */}
            <NativeBootstrap />
            {/* These render outside the router but MUST stay inside an
                ErrorBoundary — they touch localStorage, which throws in
                iOS Safari Private Browsing. Without this boundary a crash
                here would white-screen the whole app. Use the silent
                variant so a PWA/cookie widget crash doesn't produce a
                full-screen error covering the actual app content. */}
            <SilentErrorBoundary>
              <PWAInstallPrompt />
            </SilentErrorBoundary>
            <SilentErrorBoundary>
              <CookieConsent />
            </SilentErrorBoundary>
            {/* Phase 5+ — Toaster gamification (badges débloqués,
                level-up, etc.). Composant déjà existant mais
                JAMAIS monté en prod (audit). Polling 60s, silent
                error boundary pour iOS Private mode. */}
            <SilentErrorBoundary>
              <GamificationNotificationContainer />
            </SilentErrorBoundary>
          </div>
        </AssociationAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
